import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MoonStars } from "@phosphor-icons/react";

import type { WorkoutItem } from "../../planApi";
import { SLEEP_EXTRA_TSB } from "../../lib/scienceThresholds";

/**
 * W-IA-2f (IA_CANON_v2 раздел 2.11) — вечерний блок Сегодня.
 *
 * После 18:00: превью завтрашней тренировки (утро начинается без
 * сюрпризов) + рекомендация сна: базовые 8ч, +30′ при отрицательном
 * TSB ниже −10 (усталость требует сна — консервативная эвристика,
 * настоящий recovery_recommendation-endpoint — ТД).
 * Pure по входам: hour/tomorrow/tsb приходят снаружи — тестируется без моков.
 */
export function TodayEveningBlock({
  hour,
  tomorrow,
  tsb,
}: {
  hour: number;
  tomorrow: WorkoutItem | null;
  tsb: number | null;
}) {
  const { t } = useTranslation("dashboard");

  const extraSleep = tsb != null && tsb < SLEEP_EXTRA_TSB;
  const sleepGoal = extraSleep ? "8:30" : "8:00";

  // W-FIX-C: днём блок не исчезает (атлету надо знать что готовить на
  // завтра — гели/обувь/подъём), а сжимается в одну строку без цели сна.
  if (hour < 18) {
    return (
      <div
        className="indigo-card px-3 py-2 flex items-center gap-2.5"
        data-testid="today-evening-compact"
      >
        <MoonStars size={15} className="text-fg-muted shrink-0" aria-hidden />
        <span className="text-[11px] text-fg-secondary min-w-0 truncate">
          {t("evening.tomorrow_label", { defaultValue: "Завтра" })}
          {": "}
          {tomorrow ? (
            <Link to={`/workout/${tomorrow.id}`} className="text-fg-primary hover:underline">
              {tomorrow.title ||
                t("evening.tomorrow_workout", { defaultValue: "Тренировка" })}
              {tomorrow.planned_duration_minutes != null &&
                ` · ${Math.round(tomorrow.planned_duration_minutes)}′`}
            </Link>
          ) : (
            // Короткая форма: строка уже начинается с «Завтра: », и полный
            // ключ rest_day давал «Завтра: Завтра — отдых».
            t("evening.rest_day_short", { defaultValue: "отдых" })
          )}
        </span>
      </div>
    );
  }

  return (
    <div className="indigo-card px-3 py-2.5 flex items-start gap-3" data-testid="today-evening-block">
      <MoonStars size={18} className="text-fg-secondary shrink-0 mt-0.5" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-fg-muted">
          {t("evening.eyebrow", { defaultValue: "ВЕЧЕР · ЗАВТРА" })}
        </div>
        <div className="text-sm text-fg-primary mt-0.5">
          {tomorrow ? (
            <Link to={`/workout/${tomorrow.id}`} className="hover:underline">
              {tomorrow.title ||
                t("evening.tomorrow_workout", { defaultValue: "Тренировка" })}
              {tomorrow.planned_duration_minutes != null &&
                ` · ${Math.round(tomorrow.planned_duration_minutes)}′`}
            </Link>
          ) : (
            t("evening.rest_day", { defaultValue: "Завтра — отдых" })
          )}
        </div>
        <div className="text-[11px] font-mono mt-0.5 text-fg-muted">
          {t("evening.sleep_goal", { defaultValue: "цель сна {{h}}", h: sleepGoal })}
          {extraSleep && (
            <span className="text-warn">
              {" · "}
              {t("evening.sleep_reason", { defaultValue: "нагрузка высокая" })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
